// GQL
import { client } from '../client';
import { FETCH_MATERIALS } from '../queries/actions/materials';

/**
 * Revalidación en vuelo, para no pedir el catálogo N veces cuando el loader se
 * ejecuta varias veces por navegación.
 */
let inFlightRevalidation: Promise<void> | null = null;

/** Forma que devuelve `FETCH_MATERIALS`, tal cual la consumen los loaders. */
export type MaterialCatalogEntry = {
  id: string;
  materialId: string;
  name: string;
  typeOfMeasurement: string;
  codification: string;
  subCodification: string;
  measureTypeIDs: string[];
  codificationIDs: string[];
  subCodificationIDs: string[];
  teamIDs?: string[];
  /** Baja lógica del catálogo. AUSENTE o null = ACTIVO. */
  isActive?: boolean | null;
};

/**
 * Catálogo de materiales para los pasos de la actuación, en
 * **stale-while-revalidate**: se pinta al instante lo que hay en caché y se
 * revalida en segundo plano.
 *
 * El problema que resuelve: la asignación de equipos de un material se decide
 * en Gestión, normalmente en OTRO dispositivo, y la caché de Apollo está
 * PERSISTIDA con un TTL de 3 h. Con el `cache-first` por defecto, una tablet ya
 * abierta seguía sirviendo los `teamIDs` viejos hasta que caducaran o hasta
 * recargar: restringir un material tardaba ese rato en llegar a quien tiene que
 * verlo restringido, y liberarlo, igual.
 *
 * 🔴 `cache-and-network` **no se puede usar aquí**: `client.query` lo rechaza
 * con un invariant de Apollo (verificado en la 3.14), porque devuelve un único
 * resultado y esa policy emite dos. Solo lo admite `watchQuery`. De ahí que el
 * patrón se monte a mano con dos llamadas.
 *
 * 🔴 Comportamiento ACEPTADO, no una carencia pendiente: el cambio NO repinta la
 * lista que ya está en pantalla — entra la próxima vez que se abre el paso.
 *
 * El loader entrega datos estáticos. Se intentó forzar el repintado avisando
 * desde aquí y pidiendo `revalidate()` al router, y no se consiguió (el aviso se
 * emite antes de que la pantalla exista, porque lo arranca el propio loader). La
 * única vía que quedaba era leer la caché de forma reactiva con `useQuery`, y el
 * usuario la **descartó** el 2026-08-15: el patrón arquitectónico de esta app
 * son los loaders, y no se introduce una excepción en un paso del asistente.
 *
 * O sea: NO pasar esto a `useQuery` «para terminarlo». La ventana de desfase es
 * una entrada al paso, frente a las 3 h del TTL que había antes.
 *
 * `cache-only` en la primera lectura es deliberado, NO `cache-first`:
 * `cache-first` saldría a la red cuando la caché está incompleta y volvería a
 * lanzar, tirando el loader entero en una tablet sin señal. Y `materials` puede
 * llegar `undefined` de una respuesta parcial, de ahí el `?? []`.
 */
export const fetchMaterialsCatalog = async (): Promise<
  MaterialCatalogEntry[]
> => {
  const cached = await readFromCache();

  if (cached.length) {
    // Revalidación en segundo plano: NO se espera, así que abrir el paso sigue
    // siendo instantáneo. Deja la caché fresca para la próxima entrada; la
    // lista que ya está en pantalla no se repinta (ver el límite de arriba).
    void revalidateInBackground();

    return cached;
  }

  // Caché vacía (primer arranque, o purgada): aquí sí hay que esperar a la red.
  try {
    const { data } = await client.query({
      query: FETCH_MATERIALS,
      fetchPolicy: 'network-only',
    });
    return data?.materials ?? [];
  } catch (err) {
    console.log('[materials] sin catálogo: ni caché ni red', err);
    return [];
  }
};

/**
 * Pide el catálogo a la RED —es lo que la hace revalidación y no una segunda
 * lectura de lo mismo— y deja la caché lista para la próxima entrada al paso.
 *
 * No se espera nunca: abrir el paso sigue siendo instantáneo. Los fallos se
 * tragan a propósito — sin cobertura esto no debe ensuciar la consola ni tumbar
 * el paso, que ya tiene sus datos.
 *
 * Se deduplica porque el loader se ejecuta varias veces por navegación (medido:
 * hasta 6 en desarrollo) y no hace falta pedir el catálogo una vez por cada una.
 */
const revalidateInBackground = (): Promise<void> => {
  if (inFlightRevalidation) return inFlightRevalidation;

  inFlightRevalidation = client
    .query({ query: FETCH_MATERIALS, fetchPolicy: 'network-only' })
    .then(() => undefined)
    .catch(() => undefined)
    .finally(() => {
      inFlightRevalidation = null;
    });

  return inFlightRevalidation;
};

/** Lectura que NUNCA sale a la red, para que sin cobertura no lance. */
const readFromCache = async (): Promise<MaterialCatalogEntry[]> => {
  try {
    const { data } = await client.query({
      query: FETCH_MATERIALS,
      fetchPolicy: 'cache-only',
    });
    return data?.materials ?? [];
  } catch {
    // Caché incompleta para esta query: se trata como vacía.
    return [];
  }
};
